import React from 'react';
import { Link } from 'react-router-dom';

import { ArrowLeftIcon } from '../../assets';

/**
 * The three cards under the course's name — Figma nodes 16383:20911,
 * 16383:20902 and 16383:20891.
 *
 * One component, three tones, because the design draws one card three times:
 * a 40px glyph badge on a 10px radius, a bold title over a caption, and an arrow
 * at the far end, inside a 16px-radius box.
 *
 *  - `bank` — outlined in the brand blue, the badge in blue/100.
 *  - `periodic` — outlined in purple/400, the badge in purple/50, the glyph and
 *    the arrow in purple/300.
 *  - `mock` — the green gradient and its green glow, everything on it white.
 *
 * The two outlined cards take 15px of padding and the gradient one 16: Figma
 * draws the hairline inside the box and CSS outside it, so that is what keeps
 * them at the design's 76px and 80px.
 *
 * The badge is written first and the arrow last, so in Arabic the badge sits at
 * the right and the arrow points left, onward; English mirrors both, and the
 * arrow turns round with it. The gradient is physical — lighter at the left in
 * either language — because the design's is, and a gradient angle does not
 * follow `dir`.
 */
const TONES = {
    bank: {
        card: 'tw-border tw-border-solid tw-border-v2-brand tw-bg-white tw-p-[15px]',
        badge: 'tw-bg-v2-student-bank-tint tw-text-v2-brand',
        arrow: 'tw-text-v2-brand',
        title: 'tw-leading-[1.6] tw-text-v2-student-name',
        lead: 'tw-leading-[1.5] tw-text-v2-student-nav',
        inset: 'tw-ps-[10px]',
    },
    periodic: {
        card: 'tw-border tw-border-solid tw-border-v2-student-periodic tw-bg-white tw-p-[15px]',
        badge: 'tw-bg-v2-student-periodic-tint tw-text-v2-student-periodic-glyph',
        arrow: 'tw-text-v2-student-periodic-glyph',
        title: 'tw-leading-[1.6] tw-text-v2-student-name',
        lead: 'tw-leading-[1.5] tw-text-v2-student-nav',
        inset: 'tw-ps-[12px]',
    },
    mock: {
        card:
            'tw-self-stretch tw-border-0 tw-bg-gradient-to-r tw-from-v2-student-mock-from ' +
            'tw-to-v2-student-mock-to tw-p-[16px] tw-shadow-v2-mock',
        badge: 'tw-bg-white tw-text-v2-student-mock-from',
        arrow: 'tw-text-white',
        title: 'tw-leading-normal tw-text-white',
        lead: 'tw-leading-normal tw-text-white',
        inset: 'tw-ps-[12px]',
    },
};

const ActionCard = ({ tone = 'bank', to, onClick, icon, title, lead, className = '' }) => {
    const style = TONES[tone] ?? TONES.bank;

    const classes =
        'tw-flex tw-items-center tw-justify-between tw-rounded-[16px] tw-text-start ' +
        'tw-transition hover:tw--translate-y-0.5 focus-visible:tw-outline-none ' +
        'focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand focus-visible:tw-ring-offset-2 ' +
        `${style.card} ${className}`;

    const body = (
        <>
            <span aria-hidden className={`tw-flex tw-size-[40px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] ${style.badge}`}>
                {icon}
            </span>

            <span className={`tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-start tw-gap-[4px] ${style.inset}`}>
                <span className={`tw-max-w-full tw-truncate tw-text-[14px] tw-font-bold ${style.title}`}>{title}</span>
                <span className={`tw-max-w-full tw-truncate tw-text-[12px] ${style.lead}`}>{lead}</span>
            </span>

            <ArrowLeftIcon width={16} height={16} aria-hidden className={`tw-shrink-0 ltr:tw-rotate-180 ${style.arrow}`} />
        </>
    );

    if (to) {
        return <Link to={to} className={classes}>{body}</Link>;
    }

    return (
        <button type="button" onClick={onClick} className={`${classes} tw-cursor-pointer`}>
            {body}
        </button>
    );
};

export default ActionCard;
